/**
 * @fileoverview Meeting State Context Provider
 *
 * Single source of truth for in-meeting UI data coming from
 * GET /api/zoom/state/:meetingId (state engine snapshot).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { isKnownBotDisplayName } from "../../../src/shared/knownBotsDisplayName";
import config from "../config";
import api from "../services/api";
import { createModerationWebSocket, ConnectionState, MessageTypes } from "../services/websocket";
import { logOutputsPushEvent } from "../utils/logger";
import { useBanners } from "../components/BannerNotification";
import { processUiUpdateBanner } from "../utils/bannerValidation";
import type { InterimMeetingAnalysisDTO, ScorecardDimension } from "../types/interimAnalysis";

/**
 * IANA zone sent on each GET /api/zoom/state poll as `viewerTz` so the server can format
 * `lifecycle.displayTimezone` for headless screenshare (Playwright uses the host zone, not the viewer's).
 * `?viewerTz=` in the page URL overrides the device default (ops / deep links).
 */
function resolveViewerTimezoneForApi(): string {
  if (typeof window !== "undefined") {
    const fromUrl = new URLSearchParams(window.location.search).get("viewerTz");
    if (fromUrl) {
      const t = fromUrl.trim();
      if (t) {
        try {
          Intl.DateTimeFormat(undefined, { timeZone: t });
          return t;
        } catch {
          // ignore invalid override
        }
      }
    }
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export type MeetingLifecycleStatus =
  | "created"
  | "starting"
  | "live"
  | "ending"
  | "ended"
  | "error"
  | string;

export type MeetingLifecycle = {
  status: MeetingLifecycleStatus;
  startedAtMs: number | null;
  /** Server-computed meeting elapsed ms (when live). Use this for CallTimer. */
  meetingElapsedMs?: number | null;
  /** Calendar-scheduled start time (epoch ms), when available. */
  scheduledStartMs?: number | null;
  /** Calendar-scheduled end time (epoch ms), when available. */
  scheduledEndMs?: number | null;
  /**
   * IANA timezone from API payload (organizer or API host fallback).
   */
  serverTimezone?: string | null;
  /**
   * IANA zone for wall-clock UI when the client cannot rely on local `Intl` (e.g. headless
   * screenshare). Set from last `?viewerTz=` poll; falls back to organizer / server in the builder.
   */
  displayTimezone?: string | null;
};

export type MeetingParticipantTalk = {
  spokeMsTotal: number;
  spokeTurnsTotal?: number;
  lastTurnStartedAtMs?: number | null;
};

export type MeetingParticipantInterrupt = {
  interruptCount: number;
  lastInterruptAtMs?: number | null;
  softMutedUntilMs?: number | null;
  softMuteAppliedAtMs?: number | null;
  softMuteReason?: string | null;
};

export type MeetingParticipant = {
  participantId: string;
  displayName: string | null;
  isHost: boolean;
  isCoHost: boolean;
  isBot: boolean;
  talk: MeetingParticipantTalk;
  interrupt: MeetingParticipantInterrupt;
  /**
   * Epoch ms when the participant left the call (server-side), if known.
   * Mirrors GET /api/zoom/state `participants[].leftAtMs` from buildMeetingStatePayload.
   */
  leftAtMs?: number | null;
  /**
   * Epoch ms when the participant joined (approximate), if known.
   */
  joinedAtMs?: number | null;
};

export type MeetingAgendaItemStatus = "pending" | "active" | "completed" | "skipped" | string;

export type MeetingAgendaItem = {
  agendaItemId: string;
  title: string;
  /** User-facing description/sub-items (bullets or notes) for display. */
  description?: string | null;
  order: number;
  plannedDurationSec: number | null;
  status: MeetingAgendaItemStatus;
  startedAtMs: number | null;
  completedAtMs: number | null;
  timebox?: {
    remainingSec: number | null;
    overtimeSec: number | null;
  };
};

export type MeetingAgendaState = {
  items: MeetingAgendaItem[];
  activeAgendaItemId: string | null;
};

export type MeetingDecisionItem = {
  decisionId: string;
  text: string;
  agendaItemId: string | null;
  status?: string;
};

export type MeetingDecisionConsolidated = {
  agendaItemId: string | null;
  text: string;
  updatedAtMs?: number;
};

export type MeetingDecisionsState = {
  items: MeetingDecisionItem[];
  consolidated: MeetingDecisionConsolidated[];
};

export type MeetingActionItem = {
  actionId: string;
  tsMs: number;
  kind: string;
  participantId: string | null;
  agendaItemId: string | null;
  data: Record<string, unknown>;
};

export type MeetingActionsFeed = {
  items: MeetingActionItem[];
};

export type MeetingTranscriptState = {
  currentSpeaker: {
    participantId: string | null;
    startedAtMs: number | null;
  };
};

/** Confirmed outputs per agenda item (from meetingOutputs), separated by type. */
export type AgendaItemOutputs = {
  decisions: Array<{ id: string; summary: string; owner?: string }>;
  actionItems: Array<{ id: string; summary: string; owner?: string; dueDate?: string }>;
  followUps: Array<{ id: string; summary: string; owner?: string; dueDate?: string }>;
};

/** Pending (not yet confirmed) insight candidates grouped by agenda item. */
export type PendingInsightCandidate = {
  candidateId: string;
  insightType: string;
  summary: string;
  owner?: string;
  dueDate?: string;
  agendaItemId?: string | null;
  agendaItemTitle?: string | null;
};

/** A single entry in the merged participant roster (invitees + live Zoom participants). */
export type ParticipantRosterEntry = {
  rosterId: string;
  source: 'invitee' | 'live';
  displayName: string;
  /** invited = not yet joined; active = currently in meeting; left = departed. */
  status: 'invited' | 'active' | 'left';
  participantId?: string;
  talk?: { spokeMsTotal: number };
  isHost?: boolean;
  isCoHost?: boolean;
  leftAtMs?: number | null;
  joinedAtMs?: number | null;
};

/** Live screenshare content state (null = agenda mode / no active content sharing). */
export type DocumentState = {
  attachment_id: string;
  page_number: number;
  display_mode: 'agenda' | 'content';
  page_count: number;
} | null;

/** Facilitation request (user asked Rithmo for help). */
export type FacilitationRequest = {
  requestId: string;
  requestKind: string;
  rawText: string;
  status: string;
  createdAtMs: number;
  lastUpdatedAtMs: number;
  lastResponseAtMs?: number | null;
  responseText?: string | null;
  candidates?: Array<{ type: string; summary: string; owner?: string | null; dueDate?: string | null }> | null;
};

export type MeetingState = {
  meetingId: string;
  lifecycle: MeetingLifecycle;
  agenda: MeetingAgendaState;
  participants: Record<string, MeetingParticipant>;
  transcript: MeetingTranscriptState;
  decisions: MeetingDecisionsState;
  actionsFeed: MeetingActionsFeed;
  /** Confirmed outputs grouped by agendaItemId, separated into decisions, actionItems, followUps. */
  outputsByAgendaItem: Record<string, AgendaItemOutputs>;
  /** Pending outputs grouped by agendaItemId. */
  pendingCandidatesByAgendaItem: Record<string, PendingInsightCandidate[]>;
  /** Facilitation requests (user asked Rithmo for help). */
  facilitation?: { requests: FacilitationRequest[] };
  timestamp?: number | null;
  /** Meeting title from DB (available in headless Playwright mode where Zoom SDK context is null). */
  meetingTitle?: string | null;
  /** Audience mode from DB: 'internal' for internal meetings, 'external' for external. */
  audienceMode?: 'internal' | 'external' | null;
  /** Live interim analysis metrics for internal meetings in recap view. Null when not applicable. */
  interimAnalysis?: InterimMeetingAnalysisDTO | null;
  /** Live scorecard dimensions during active meetings (internal only). Null when not live or external. */
  liveScorecard?: ScorecardDimension[] | null;
  /** Merged roster: expected invitees (with status Invited/Active/Left) + unlisted live joiners. Null when not available. */
  participantRoster?: ParticipantRosterEntry[] | null;
  /** True when Rithmo's voice is suppressed ("stop speaking" mode). */
  ttsSuppressed?: boolean;
  /** Live screenshare content state. Null = agenda mode (no active PDF sharing). */
  documentState?: DocumentState;
};

type BotStatus = {
  connected: boolean;
  lastTranscript: string | null;
  streamingLatency: string | null;
  meetingState: string;
};

/** Max characters to keep in the rolling transcript buffer (append-only, trimmed from the start). */
const TRANSCRIPT_BUFFER_MAX_CHARS = 2000;
/** Characters to show in the teleprompter (last N of the buffer). */
export const ROLLING_TRANSCRIPT_DISPLAY_CHARS = 250;

type MeetingStateContextValue = {
  meetingState: MeetingState | null;
  isInitialized: boolean;
  isHost: boolean;
  meetingId: string | null;
  meetingUUID: string | null;
  userContext: Record<string, unknown> | null;
  meetingContext: Record<string, unknown> | null;
  error: string | null;
  wsConnected: boolean;
  botStatus: BotStatus;
  /** Last ROLLING_TRANSCRIPT_DISPLAY_CHARS of the rolling transcript buffer (teleprompter text). */
  rollingTranscript: string;
  debugLogs: string[];
  initZoomApp: () => Promise<void>;
  startDemoMode: () => Promise<void>;
  startMeeting: () => Promise<void>;
  advanceAgenda: (force?: boolean) => Promise<void>;
  skipAgenda: () => Promise<void>;
  confirmDecision: (decisionId: string) => Promise<void>;
  getMeetingId: () => string | null;
  clearDebugLogs: () => void;
  /** Increments when a screenshare scroll command is received; direction indicates which way. */
  screenshareOutputsScroll: { seq: number; direction: 'down' | 'up' } | null;
  /** Runtime bearer token for live-insights API, obtained via POST /api/zoom/identify. */
  insightsToken: string | null;
};

const DEFAULT_MEETING_STATE: MeetingState = {
  meetingId: "",
  lifecycle: {
    status: "created",
    startedAtMs: null,
  },
  agenda: {
    items: [],
    activeAgendaItemId: null,
  },
  participants: {},
  transcript: {
    currentSpeaker: { participantId: null, startedAtMs: null },
  },
  decisions: {
    items: [],
    consolidated: [],
  },
  actionsFeed: {
    items: [],
  },
  outputsByAgendaItem: {},
  pendingCandidatesByAgendaItem: {},
  facilitation: { requests: [] },
  timestamp: null,
};

const MeetingStateContext = createContext<MeetingStateContextValue | null>(null);

function toNumber(value: unknown, fallback: number = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toTimestamp(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function normalizeMeetingState(raw: any): MeetingState {
  const meetingId = raw?.meetingId != null ? String(raw.meetingId) : "";

  const agendaItems: MeetingAgendaItem[] = Array.isArray(raw?.agenda?.items)
    ? raw.agenda.items.map((item: any, index: number) => {
        const agendaItemId = String(item?.agendaItemId ?? item?.id ?? `item-${index}`);
        return {
          agendaItemId,
          title: String(item?.title ?? ""),
          order: Number.isFinite(Number(item?.order)) ? Number(item.order) : index,
          plannedDurationSec:
            item?.plannedDurationSec != null
              ? Number(item.plannedDurationSec)
              : item?.durationSec != null
              ? Number(item.durationSec)
              : item?.timeboxSec != null
              ? Number(item.timeboxSec)
              : null,
          status: item?.status ?? (item?.closed ? "completed" : "pending"),
          startedAtMs: item?.startedAtMs ?? null,
          completedAtMs: item?.completedAtMs ?? null,
          timebox: {
            remainingSec: item?.timebox?.remainingSec ?? null,
            overtimeSec: item?.timebox?.overtimeSec ?? null,
          },
        };
      })
    : [];

  const currentIndex = Number.isFinite(Number(raw?.agenda?.currentIndex))
    ? Number(raw.agenda.currentIndex)
    : -1;
  const activeAgendaItemId =
    (currentIndex >= 0 && agendaItems[currentIndex]?.agendaItemId) ||
    agendaItems.find((item) => item.status === "active")?.agendaItemId ||
    null;

  const participantsArray = Array.isArray(raw?.participants) ? raw.participants : [];
  const participants = participantsArray.reduce((acc: Record<string, MeetingParticipant>, p: any) => {
    const participantId = String(p?.participantId ?? p?.id ?? p?.participantUUID ?? "");
    if (!participantId) return acc;

    const role = String(p?.role ?? "").toLowerCase();
    const isHost = p?.isHost === true || role === "host";
    const isCoHost = p?.isCoHost === true || role === "co-host" || role === "cohost";

    const leftRaw = p?.leftAtMs;
    const joinedRaw = p?.joinedAtMs;
    const leftAtMs =
      leftRaw != null && Number.isFinite(Number(leftRaw)) ? Number(leftRaw) : null;
    const joinedAtMs =
      joinedRaw != null && Number.isFinite(Number(joinedRaw)) ? Number(joinedRaw) : null;

    acc[participantId] = {
      participantId,
      displayName: p?.displayName ?? p?.name ?? null,
      isHost,
      isCoHost,
      isBot: Boolean(p?.isBot ?? false),
      talk: {
        spokeMsTotal: toNumber(p?.talk?.spokeMsTotal ?? p?.speakingTime ?? 0, 0),
        spokeTurnsTotal: p?.talk?.spokeTurnsTotal ?? undefined,
        lastTurnStartedAtMs: p?.talk?.lastTurnStartedAtMs ?? null,
      },
      interrupt: {
        interruptCount: toNumber(p?.interrupt?.interruptCount ?? p?.interruptions ?? 0, 0),
        lastInterruptAtMs: p?.interrupt?.lastInterruptAtMs ?? null,
        softMutedUntilMs: p?.interrupt?.softMutedUntilMs ?? p?.softMutedUntilMs ?? null,
        softMuteAppliedAtMs: p?.interrupt?.softMuteAppliedAtMs ?? null,
        softMuteReason: p?.interrupt?.softMuteReason ?? null,
      },
      ...(leftAtMs != null ? { leftAtMs } : {}),
      ...(joinedAtMs != null ? { joinedAtMs } : {}),
    };

    return acc;
  }, {} as Record<string, MeetingParticipant>);

  const decisionsRaw = Array.isArray(raw?.decisions) ? raw.decisions : [];
  const decisionItems: MeetingDecisionItem[] = [];
  const consolidated: MeetingDecisionConsolidated[] = [];

  decisionsRaw.forEach((d: any, index: number) => {
    if (d?.decisionId || d?.status || d?.ownerParticipantId) {
      decisionItems.push({
        decisionId: String(d?.decisionId ?? `decision-${index}`),
        text: String(d?.text ?? ""),
        agendaItemId: d?.agendaItemId ?? null,
        status: d?.status ?? undefined,
      });
    } else {
      consolidated.push({
        agendaItemId: d?.agendaItemId ?? null,
        text: String(d?.text ?? ""),
        updatedAtMs: d?.updatedAtMs ?? undefined,
      });
    }
  });

  const actionsRaw = Array.isArray(raw?.recentActions)
    ? raw.recentActions
    : Array.isArray(raw?.actionsFeed?.items)
    ? raw.actionsFeed.items
    : [];

  const outputsByAgendaItemRaw = raw?.outputsByAgendaItem && typeof raw.outputsByAgendaItem === "object" ? raw.outputsByAgendaItem : {};
  const outputsByAgendaItem: Record<string, AgendaItemOutputs> = {};
  for (const [key, val] of Object.entries(outputsByAgendaItemRaw)) {
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const v = val as Record<string, unknown>;
      outputsByAgendaItem[String(key)] = {
        decisions: Array.isArray(v.decisions) ? v.decisions.map((d: any) => ({
          id: String(d?.id ?? ""),
          summary: String(d?.summary ?? ""),
          owner: d?.owner != null ? String(d.owner) : undefined,
        })) : [],
        actionItems: Array.isArray(v.actionItems) ? v.actionItems.map((a: any) => ({
          id: String(a?.id ?? ""),
          summary: String(a?.summary ?? ""),
          owner: a?.owner != null ? String(a.owner) : undefined,
          dueDate: a?.dueDate != null ? String(a.dueDate) : undefined,
        })) : [],
        followUps: Array.isArray(v.followUps) ? v.followUps.map((f: any) => ({
          id: String(f?.id ?? ""),
          summary: String(f?.summary ?? ""),
          owner: f?.owner != null ? String(f.owner) : undefined,
          dueDate: f?.dueDate != null ? String(f.dueDate) : undefined,
        })) : [],
      };
    }
  }

  const pendingCandidatesRaw =
    raw?.pendingCandidatesByAgendaItem && typeof raw.pendingCandidatesByAgendaItem === "object"
      ? raw.pendingCandidatesByAgendaItem
      : {};
  const pendingCandidatesByAgendaItem: Record<string, PendingInsightCandidate[]> = {};
  for (const [key, val] of Object.entries(pendingCandidatesRaw)) {
    if (!Array.isArray(val)) continue;
    pendingCandidatesByAgendaItem[String(key)] = val.map((c: any, index: number) => ({
      candidateId: String(c?.candidateId ?? `${String(key)}-candidate-${index}`),
      insightType: String(c?.insightType ?? ""),
      summary: String(c?.summary ?? ""),
      owner: c?.owner != null ? String(c.owner) : undefined,
      dueDate: c?.dueDate != null ? String(c.dueDate) : undefined,
      agendaItemId: c?.agendaItemId != null ? String(c.agendaItemId) : null,
      agendaItemTitle: c?.agendaItemTitle != null ? String(c.agendaItemTitle) : null,
    }));
  }

  const actionsFeedItems: MeetingActionItem[] = actionsRaw.map((action: any, index: number) => ({
    actionId: String(action?.actionId ?? action?.id ?? `action-${index}`),
    tsMs: toNumber(action?.tsMs ?? action?.createdAt ?? Date.now(), Date.now()),
    kind: String(action?.kind ?? action?.type ?? "UNKNOWN"),
    participantId: action?.participantId ?? null,
    agendaItemId: action?.agendaItemId ?? null,
    data: (action?.data && typeof action.data === "object" ? action.data : {}) as Record<string, unknown>,
  }));

  // Use only lifecycle.meetingElapsedMs (from orchestrator). Do NOT fall back to agenda.elapsedTimeMs
  // — that uses the meeting clock which flickers (0→1→0) due to calibration. When null, show --:--.
  const meetingElapsedMs =
    raw?.lifecycle?.meetingElapsedMs != null && Number.isFinite(Number(raw.lifecycle.meetingElapsedMs))
      ? Number(raw.lifecycle.meetingElapsedMs)
      : null;

  const facilitationRequests: FacilitationRequest[] = Array.isArray(raw?.facilitation?.requests)
    ? raw.facilitation.requests.map((r: any) => ({
        requestId: String(r?.requestId ?? ""),
        requestKind: String(r?.requestKind ?? ""),
        rawText: String(r?.rawText ?? ""),
        status: String(r?.status ?? "pending"),
        createdAtMs: toNumber(r?.createdAtMs, 0),
        lastUpdatedAtMs: toNumber(r?.lastUpdatedAtMs, 0),
        lastResponseAtMs: r?.lastResponseAtMs != null ? toNumber(r.lastResponseAtMs, 0) : null,
        responseText: r?.responseText ?? null,
        candidates: Array.isArray(r?.candidates)
          ? r.candidates.map((c: any) => ({
              type: String(c?.type ?? ""),
              summary: String(c?.summary ?? ""),
              owner: c?.owner ?? null,
              dueDate: c?.dueDate ?? null,
            }))
          : null,
      }))
    : [];

  return {
    meetingId,
    lifecycle: {
      status: raw?.lifecycle?.status ?? raw?.agenda?.status ?? "created",
      startedAtMs:
        toTimestamp(raw?.lifecycle?.startedAtMs) ??
        toTimestamp(raw?.meeting?.startedAtMs) ??
        toTimestamp(raw?.meeting?.startedAt) ??
        null,
      meetingElapsedMs: Number.isFinite(meetingElapsedMs) ? meetingElapsedMs : null,
      scheduledStartMs:
        raw?.lifecycle?.scheduledStartMs != null && Number.isFinite(Number(raw.lifecycle.scheduledStartMs))
          ? Number(raw.lifecycle.scheduledStartMs)
          : null,
      scheduledEndMs:
        raw?.lifecycle?.scheduledEndMs != null && Number.isFinite(Number(raw.lifecycle.scheduledEndMs))
          ? Number(raw.lifecycle.scheduledEndMs)
          : null,
      serverTimezone: typeof raw?.lifecycle?.serverTimezone === 'string' ? raw.lifecycle.serverTimezone : null,
      displayTimezone: typeof raw?.lifecycle?.displayTimezone === 'string' ? raw.lifecycle.displayTimezone : null,
    },
    agenda: {
      items: agendaItems,
      activeAgendaItemId,
    },
    participants,
    transcript: {
      currentSpeaker: {
        participantId: raw?.transcript?.currentSpeaker?.participantId ?? null,
        startedAtMs: raw?.transcript?.currentSpeaker?.startedAtMs ?? null,
      },
    },
    decisions: {
      items: decisionItems,
      consolidated,
    },
    actionsFeed: {
      items: actionsFeedItems,
    },
    outputsByAgendaItem,
    pendingCandidatesByAgendaItem,
    facilitation: { requests: facilitationRequests },
    timestamp: raw?.timestamp ?? null,
    meetingTitle: raw?.meetingTitle ?? null,
    audienceMode: raw?.audienceMode ?? null,
    interimAnalysis: raw?.interimAnalysis ?? null,
    liveScorecard: Array.isArray(raw?.liveScorecard) ? raw.liveScorecard as ScorecardDimension[] : null,
    participantRoster: Array.isArray(raw?.participantRoster) ? raw.participantRoster as ParticipantRosterEntry[] : null,
    ttsSuppressed: raw?.ttsSuppressed === true,
    documentState: (() => {
      const ds = raw?.documentState;
      if (!ds || typeof ds !== 'object') return null;
      if (ds.display_mode !== 'content' && ds.display_mode !== 'agenda') return null;
      return {
        attachment_id: String(ds.attachment_id ?? ''),
        page_number: Number(ds.page_number ?? 1),
        display_mode: ds.display_mode as 'agenda' | 'content',
        page_count: Number(ds.page_count ?? 0),
      };
    })(),
  };
}

export function MeetingStateProvider({ children }: { children: React.ReactNode }) {
  const { addBanner, removeBannersByType } = useBanners();
  const [meetingState, setMeetingState] = useState<MeetingState | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [userContext, setUserContext] = useState<Record<string, unknown> | null>(null);
  const [meetingContext, setMeetingContext] = useState<Record<string, unknown> | null>(null);
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [meetingUUID, setMeetingUUID] = useState<string | null>(null);
  const [insightsToken, setInsightsToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [botStatus, setBotStatus] = useState<BotStatus>({
    connected: false,
    lastTranscript: null,
    streamingLatency: null,
    meetingState: "Waiting for participants...",
  });
  /** Rolling buffer: each SPEAKING_UPDATE/TRANSCRIPT appends; we keep last TRANSCRIPT_BUFFER_MAX_CHARS. */
  const [transcriptBuffer, setTranscriptBuffer] = useState<string>("");
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const [screenshareOutputsScroll, setScreenshareOutputsScroll] = useState<{ seq: number; direction: 'down' | 'up' } | null>(null);
  const screenshareScrollSeqRef = useRef(0);

  const seenBannerIdsRef = useRef<Set<string>>(new Set());
  const wsManagerRef = useRef<ReturnType<typeof createModerationWebSocket> | null>(null);
  const meetingContextRef = useRef<Record<string, unknown> | null>(null);
  const userContextRef = useRef<Record<string, unknown> | null>(null);
  const meetingIdRef = useRef<string | null>(null);
  const meetingUUIDRef = useRef<string | null>(null);
  const initInProgressRef = useRef(false);
  const initCompletedRef = useRef(false);
  const meetingStatePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const backendSyncRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const outputsPushActiveRef = useRef(false);

  useEffect(() => {
    meetingContextRef.current = meetingContext;
  }, [meetingContext]);

  useEffect(() => {
    userContextRef.current = userContext;
  }, [userContext]);

  useEffect(() => {
    meetingIdRef.current = meetingId;
  }, [meetingId]);

  useEffect(() => {
    meetingUUIDRef.current = meetingUUID;
  }, [meetingUUID]);

  const debugLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    const logMessage = `${timestamp}: ${message}`;
    setDebugLogs((prev: string[]) => {
      const next = [...prev, logMessage];
      try {
        localStorage.setItem("zoomAppLogs", JSON.stringify(next.slice(-config.ui.debugLogMaxEntries)));
      } catch {
        // ignore storage errors
      }
      return next.slice(-config.ui.debugLogMaxEntries);
    });
  }, []);

  const clearDebugLogs = useCallback(() => {
    try {
      localStorage.removeItem("zoomAppLogs");
    } catch {
      // ignore
    }
    setDebugLogs([]);
  }, []);

  const getMeetingId = useCallback(() => {
    const ctx = meetingContextRef.current as any;
    const rawMeetingId = ctx?.meetingNumber ?? ctx?.meetingId ?? ctx?.meetingID ?? ctx?.id ?? null;
    const rawMeetingUUID = ctx?.meetingUUID ?? null;
    return rawMeetingId != null ? String(rawMeetingId) : rawMeetingUUID != null ? String(rawMeetingUUID) : null;
  }, []);

  const updateBotStatus = useCallback((updates: Partial<BotStatus>) => {
    setBotStatus((prev: BotStatus) => ({ ...prev, ...updates }));
  }, []);

  const handleMuteCommand = useCallback(async (msg: any) => {
    const participantUUID = msg.offender?.participantUUID || msg.participantUUID;
    const displayName = msg.offender?.displayName || msg.participantName || "participant";
    if (!participantUUID || !window.zoomSdk) return;
    try {
      await window.zoomSdk.toggleParticipantMediaAudio({ participantUUIDs: [participantUUID], audio: false });
      await window.zoomSdk.showNotification({
        type: "info",
        title: "Auto-mute",
        message: `Muted ${displayName} for repeated interruptions.`,
      });
    } catch (err: unknown) {
      debugLog(`[Mute] Error: ${String((err as Error)?.message ?? err)}`);
    }
  }, [debugLog]);

  const handleUnmuteCommand = useCallback(async (msg: any) => {
    const participantUUID = msg.offender?.participantUUID || msg.participantUUID;
    const displayName = msg.offender?.displayName || msg.participantName || "participant";
    if (!participantUUID || !window.zoomSdk) return;
    try {
      await window.zoomSdk.toggleParticipantMediaAudio({ participantUUIDs: [participantUUID], audio: true });
      await window.zoomSdk.showNotification({
        type: "info",
        title: "Auto-unmute",
        message: `Unmuted ${displayName}.`,
      });
    } catch (err: unknown) {
      debugLog(`[Unmute] Error: ${String((err as Error)?.message ?? err)}`);
    }
  }, [debugLog]);

  /**
   * Apply GET /api/zoom/state (or equivalent WS push) payload to React state.
   * Shared by HTTP polling and MEETING_STATE_UPDATE so participant presence stays in sync.
   */
  const applyMeetingStateFromServerRaw = useCallback((raw: any, fallbackMeetingId?: string | null) => {
    if (!raw || typeof raw !== "object") return;
    const normalized = normalizeMeetingState(raw);

    // Prefer the canonical id returned by the server (e.g. _occ_ occurrence id).
    // Never let a demo-mode placeholder override a real meeting id already in use.
    const serverMeetingId =
      normalized.meetingId && String(normalized.meetingId).trim() !== ""
        ? normalized.meetingId
        : null;
    const currentId = meetingIdRef.current;
    const isCurrentIdReal = currentId != null && !currentId.startsWith("demo-");
    const isServerIdReal = serverMeetingId != null && !serverMeetingId.startsWith("demo-");

    const finalMeetingId =
      // If server returned a real id, always prefer it (handles numeric→_occ_ upgrade)
      (isServerIdReal ? serverMeetingId : null) ||
      // Otherwise fall back: keep current real id, then fallback arg, then Zoom SDK
      (isCurrentIdReal ? currentId : null) ||
      fallbackMeetingId ||
      currentId ||
      getMeetingId();

    if (!finalMeetingId) return;
    if (finalMeetingId !== meetingIdRef.current) {
      setMeetingId(finalMeetingId);
    }
    setMeetingState(() => ({
      ...DEFAULT_MEETING_STATE,
      ...normalized,
      meetingId: finalMeetingId,
    }));

    const lifecycleStatus = normalized.lifecycle?.status ?? raw?.lifecycle?.status;
    if (lifecycleStatus === "ended") {
      debugLog("[MeetingState] Meeting ended, stopping polls");
      if (meetingStatePollRef.current) {
        clearInterval(meetingStatePollRef.current);
        meetingStatePollRef.current = null;
      }
      if (backendSyncRef.current) {
        clearInterval(backendSyncRef.current);
        backendSyncRef.current = null;
      }
    }

    // Deliver banners from the poll response (headless Playwright share-capture mode has no WebSocket)
    if (Array.isArray(raw?.recentBanners)) {
      raw.recentBanners.forEach((b: any) => {
        if (!b?.id || !b?.type || seenBannerIdsRef.current.has(b.id)) return;
        seenBannerIdsRef.current.add(b.id);
        // Intercept silent scroll signals before they reach the banner system.
        if (b.type === 'screenshare_outputs_scroll_down' || b.type === 'screenshare_outputs_scroll_up') {
          const direction = b.type === 'screenshare_outputs_scroll_down' ? 'down' : 'up';
          screenshareScrollSeqRef.current += 1;
          setScreenshareOutputsScroll({ seq: screenshareScrollSeqRef.current, direction });
          return;
        }
        addBanner({ type: b.type, title: b.title, message: b.message, autoDismiss: true });
      });
    }

    if (raw?.lastTranscript != null || raw?.streamingLatencyMs != null) {
      const updates: { lastTranscript?: string; streamingLatency?: string } = {};
      if (typeof raw.lastTranscript === "string") {
        updates.lastTranscript = raw.lastTranscript;
        setTranscriptBuffer((prev) => (prev ? prev : raw.lastTranscript));
      }
      if (typeof raw.streamingLatencyMs === "number" && Number.isFinite(raw.streamingLatencyMs)) {
        updates.streamingLatency = `${raw.streamingLatencyMs} ms`;
      }
      if (Object.keys(updates).length > 0) updateBotStatus(updates);
    }
  }, [addBanner, debugLog, getMeetingId, updateBotStatus]);

  const connectToBackend = useCallback((meetingCtx: Record<string, unknown> | null, userCtx: Record<string, unknown> | null) => {
    return new Promise<void>((resolve, reject) => {
      const meetingUUIDValue = (meetingCtx as any)?.meetingUUID || (meetingContextRef.current as any)?.meetingUUID;
      const hostParticipantUUID = (userCtx as any)?.participantUUID || (userContextRef.current as any)?.participantUUID;

      if (!meetingUUIDValue || !hostParticipantUUID) {
        debugLog("[Backend] Cannot connect: missing meetingUUID or hostParticipantUUID");
        reject(new Error("Missing meeting or user context"));
        return;
      }

      wsManagerRef.current = createModerationWebSocket(meetingUUIDValue, hostParticipantUUID, {
        onStateChange: (newState) => {
          if (newState === ConnectionState.CONNECTED) {
            setWsConnected(true);
            debugLog("[Backend] ✅ Connected");
            resolve();
          } else if (newState === ConnectionState.DISCONNECTED || newState === ConnectionState.ERROR) {
            if (outputsPushActiveRef.current) {
              outputsPushActiveRef.current = false;
              logOutputsPushEvent("poll_resume_after_disconnect", {
                meetingId: meetingIdRef.current ?? "unknown",
                outputsPushActive: false,
              });
            }
            setWsConnected(false);
          }
        },
        onMessage: (msg: any) => {
          if (msg.type === MessageTypes.MUTE_USER) {
            handleMuteCommand(msg);
          } else if (msg.type === MessageTypes.UNMUTE_USER) {
            handleUnmuteCommand(msg);
          } else if (msg.type === MessageTypes.SPEAKING_UPDATE) {
            const chunk = (msg.transcriptText || "").trim();
            if (chunk) {
              setTranscriptBuffer((prev) => {
                const next = prev ? prev + " " + chunk : chunk;
                return next.length > TRANSCRIPT_BUFFER_MAX_CHARS ? next.slice(-TRANSCRIPT_BUFFER_MAX_CHARS) : next;
              });
            }
            const streamingLatency = msg.latencyMs != null ? `${msg.latencyMs} ms` : null;
            updateBotStatus({
              connected: true,
              lastTranscript: msg.transcriptText || null,
              ...(streamingLatency != null && { streamingLatency }),
              meetingState: "Bot connected, listening...",
            });
          } else if (msg.type === "UI_UPDATE" && msg.payload) {
            const meetingIdForBanner = meetingIdRef.current ?? getMeetingId() ?? "unknown";
            // Intercept silent scroll signals before they reach the banner system.
            const bannerType = msg.payload?.type;
            if (bannerType === 'screenshare_outputs_scroll_down' || bannerType === 'screenshare_outputs_scroll_up') {
              const direction = bannerType === 'screenshare_outputs_scroll_down' ? 'down' : 'up';
              screenshareScrollSeqRef.current += 1;
              setScreenshareOutputsScroll({ seq: screenshareScrollSeqRef.current, direction });
            } else if (msg.payload?.dismiss === true) {
              // Dismiss signal — remove all banners of this type immediately.
              const dismissType = msg.payload?.type;
              if (dismissType) removeBannersByType(dismissType);
            } else {
              // Dedup by stable bannerId (set by backend publishUiUpdate) — prevents the same
              // banner from appearing twice when a WS push and a recentBanners poll both fire.
              const bannerId = typeof msg.payload?.bannerId === 'string' ? msg.payload.bannerId : null;
              if (bannerId && seenBannerIdsRef.current.has(bannerId)) {
                // already shown via another path, skip
              } else {
                if (bannerId) seenBannerIdsRef.current.add(bannerId);
                processUiUpdateBanner(msg.payload, meetingIdForBanner, addBanner);
              }
            }
          } else if (msg.type === MessageTypes.TRANSCRIPT || msg.transcript || msg.text) {
            const chunk = String(msg.transcript || msg.text || "").trim();
            if (chunk) {
              setTranscriptBuffer((prev) => {
                const next = prev ? prev + " " + chunk : chunk;
                return next.length > TRANSCRIPT_BUFFER_MAX_CHARS ? next.slice(-TRANSCRIPT_BUFFER_MAX_CHARS) : next;
              });
            }
            updateBotStatus({ lastTranscript: chunk || null });
          } else if (msg.type === MessageTypes.MEETING_STATE_UPDATE && msg.payload) {
            applyMeetingStateFromServerRaw(msg.payload);
          } else if (msg.type === MessageTypes.OUTPUTS_UPDATE && msg.payload?.outputsByAgendaItem) {
            const outputsByAgendaItem = msg.payload.outputsByAgendaItem as Record<string, AgendaItemOutputs>;
            const outputsCount = Object.values(outputsByAgendaItem).reduce(
              (s, o) => s + o.decisions.length + o.actionItems.length + o.followUps.length,
              0
            );
            const isFirst = !outputsPushActiveRef.current;
            outputsPushActiveRef.current = true;
            if (isFirst) {
              logOutputsPushEvent("first_outputs_update", {
                meetingId: meetingIdRef.current ?? "unknown",
                outputsPushActive: true,
              });
            }
            const serverTs = msg.serverTimestamp ?? msg.timestamp;
            logOutputsPushEvent("outputs_update_applied", {
              meetingId: meetingIdRef.current ?? "unknown",
              outputsPushActive: true,
              outputsCount,
              latencyFromServerTimestamp: serverTs != null ? Date.now() - Number(serverTs) : undefined,
            });
            setMeetingState((prev) => {
              if (!prev) return prev;
              return { ...prev, outputsByAgendaItem };
            });
          }
        },
        onError: (error) => {
          debugLog("[Backend] ❌ Connection error");
          reject(error);
        },
      });
    });
  }, [addBanner, removeBannersByType, applyMeetingStateFromServerRaw, debugLog, getMeetingId, handleMuteCommand, handleUnmuteCommand, updateBotStatus]);

  const fetchMeetingState = useCallback(async (meetingIdOverride?: string) => {
    const resolvedMeetingId = meetingIdOverride || meetingIdRef.current || getMeetingId();
    if (!resolvedMeetingId) return;

    try {
      const response = await api.getMeetingState(resolvedMeetingId, {
        viewerTimezone: resolveViewerTimezoneForApi(),
      });
      const raw = (response as any)?.data ?? response;
      applyMeetingStateFromServerRaw(raw, resolvedMeetingId);
    } catch (err: unknown) {
      debugLog(`[MeetingState] Fetch error: ${String((err as Error)?.message ?? err)}`);
    }
  }, [applyMeetingStateFromServerRaw, debugLog]);

  const fetchParticipantsForSync = useCallback(async () => {
    if (!window.zoomSdk) return [];
    // getMeetingParticipants() requires host or co-host (Zoom SDK 80012 require_meeting_role).
    // Attendees get participants from backend state (C++ bot roster when bot is host).
    if (!isHost) return [];
    try {
      const { participants } = await window.zoomSdk.getMeetingParticipants();
      const isBotByName = (name: string) => isKnownBotDisplayName(name);
      return (participants || [])
        .map((p: any) => {
          const participantUUID = String(p?.participantUUID ?? "").trim();
          if (!participantUUID) return null;
          const displayName = p.screenName || p.displayName || p.userName || "Unknown";
          return {
            participantUUID,
            displayName,
            role: p.role,
            isBot: isBotByName(displayName),
          };
        })
        .filter(Boolean);
    } catch (err: unknown) {
      debugLog(`[Participants] Zoom SDK error: ${String((err as Error)?.message ?? err)}`);
      return [];
    }
  }, [debugLog, isHost]);

  const syncParticipantsToBackend = useCallback(async () => {
    const participants = await fetchParticipantsForSync();
    if (!participants.length) return;
    if (wsManagerRef.current?.isConnected()) {
      wsManagerRef.current.send({
        type: MessageTypes.PARTICIPANT_LIST,
        meetingUUID: meetingUUIDRef.current,
        participants: participants.map((p: { participantUUID: string; displayName: string; role?: string }) => ({
          participantUUID: p.participantUUID,
          displayName: p.displayName,
          role: p.role,
        })),
      });
    }
  }, [fetchParticipantsForSync]);

  const startMeeting = useCallback(async () => {
    const resolvedMeetingId = getMeetingId();
    if (!resolvedMeetingId) return;
    try {
      await api.startAgenda(resolvedMeetingId);
      await fetchMeetingState(resolvedMeetingId);
      // Sync participants so backend can resolve display names for hard mute
      await syncParticipantsToBackend();
      // Intro TTS ("Hi everyone. I'm Rithmo...") is played by the backend via bot — same path as voice command and auto-start
    } catch (err: unknown) {
      setError(`Failed to start meeting: ${String((err as Error)?.message ?? err)}`);
    }
  }, [fetchMeetingState, getMeetingId, syncParticipantsToBackend]);

  const advanceAgenda = useCallback(async (force: boolean = false) => {
    const resolvedMeetingId = getMeetingId();
    if (!resolvedMeetingId) return;
    try {
      await api.advanceAgenda(resolvedMeetingId, force);
      await fetchMeetingState(resolvedMeetingId);
    } catch (err: unknown) {
      debugLog(`[Agenda] Advance error: ${String((err as Error)?.message ?? err)}`);
    }
  }, [debugLog, fetchMeetingState, getMeetingId]);

  const skipAgenda = useCallback(async () => {
    const resolvedMeetingId = getMeetingId();
    if (!resolvedMeetingId) return;
    try {
      // force: true = skip even when decision required (proceed without a decision)
      await api.skipAgendaItem(resolvedMeetingId, true);
      await fetchMeetingState(resolvedMeetingId);
    } catch (err: unknown) {
      debugLog(`[Agenda] Skip error: ${String((err as Error)?.message ?? err)}`);
    }
  }, [debugLog, fetchMeetingState, getMeetingId]);

  const confirmDecision = useCallback(async (decisionId: string) => {
    const resolvedMeetingId = getMeetingId();
    if (!resolvedMeetingId || !decisionId) return;
    try {
      await api.updateMeetingState(resolvedMeetingId, { action: "confirm_decision", data: { decisionId } });
      await fetchMeetingState(resolvedMeetingId);
    } catch (err: unknown) {
      debugLog(`[Decision] Confirm error: ${String((err as Error)?.message ?? err)}`);
    }
  }, [debugLog, fetchMeetingState, getMeetingId]);

  const initZoomApp = useCallback(async () => {
    if (initInProgressRef.current || initCompletedRef.current) return;
    initInProgressRef.current = true;

    debugLog("[Init] Starting Zoom App initialization...");

    try {
      const params = new URLSearchParams(window.location.search);
      const urlMeetingId = params.get('meetingId');
      const shareCapture = params.get('shareCapture') === '1';

      // Headless screen-share capture (Playwright): shareCapture=1 forces polling-only init
      // even when window.zoomSdk exists, so the shared frame matches the in-meeting dashboard
      // data instead of a broken / empty SDK path outside Zoom.
      if (urlMeetingId && (shareCapture || !window.zoomSdk)) {
        setMeetingId(urlMeetingId);
        setIsInitialized(true);
        await fetchMeetingState(urlMeetingId);
        initCompletedRef.current = true;
        return;
      }

      if (!window.zoomSdk) {
        throw new Error("Zoom SDK not loaded");
      }

      await window.zoomSdk.config({
        capabilities: [
          "getUserContext",
          "getMeetingContext",
          "getMeetingUUID",
          "getJoinUrl",
          "getMeetingParticipants",
          "onParticipantChange",
          "toggleParticipantMediaAudio",
          "showNotification",
          "sendMessage",
          "onMessage",
        ],
        popoutSize: { width: 560, height: 420 },
      });

      let userCtx: Record<string, unknown> | null = null;
      let meetingCtx: Record<string, unknown> | null = null;

      try {
        userCtx = await window.zoomSdk.getUserContext();
      } catch (err: unknown) {
        debugLog(`[Zoom SDK] getUserContext failed: ${String((err as Error)?.message ?? err)}`);
      }

      try {
        meetingCtx = await window.zoomSdk.getMeetingContext();
      } catch (err: unknown) {
        debugLog(`[Zoom SDK] getMeetingContext failed: ${String((err as Error)?.message ?? err)}`);
      }

      try {
        const meetingUUIDResponse = await window.zoomSdk.getMeetingUUID();
        if (meetingCtx) {
          (meetingCtx as { meetingUUID?: string }).meetingUUID = meetingUUIDResponse?.meetingUUID || (meetingCtx as { meetingUUID?: string }).meetingUUID;
        } else {
          meetingCtx = { meetingUUID: meetingUUIDResponse?.meetingUUID } as Record<string, unknown>;
        }
      } catch (err: unknown) {
        debugLog(`[Zoom SDK] getMeetingUUID failed: ${String((err as Error)?.message ?? err)}`);
      }

      setUserContext(userCtx || {});
      setMeetingContext(meetingCtx || {});

      const userRole = (userCtx as { role?: string } | null)?.role;
      setIsHost(userRole === "host" || userRole === "coHost");

      // Prefer meetingNumber (numeric ID) for API calls; fall back to meetingUUID when unavailable.
      // Participants may only receive meetingUUID from the Zoom SDK, but the backend can resolve
      // UUID to the meeting when there is a single active meeting.
      const resolvedMeetingUUID = meetingCtx ? String((meetingCtx as any).meetingUUID ?? "") : "";
      const resolvedMeetingId = meetingCtx
        ? String(
            (meetingCtx as any).meetingNumber ??
              (meetingCtx as any).meetingId ??
              (meetingCtx as any).meetingID ??
              (meetingCtx as any).id ??
              resolvedMeetingUUID ??
              ""
          )
        : resolvedMeetingUUID || "";
      setMeetingId(resolvedMeetingId || resolvedMeetingUUID || null);
      setMeetingUUID(resolvedMeetingUUID || null);

      setIsInitialized(true);

      await fetchMeetingState(resolvedMeetingId || resolvedMeetingUUID || undefined);

      // Attempt to obtain a bearer token for live-insights API by identifying
      // the Zoom SDK user against the database (name-based matching).
      const effectiveMeetingId = resolvedMeetingId || resolvedMeetingUUID || null;
      const screenName = (userCtx as { screenName?: string } | null)?.screenName ?? '';
      const participantUUID = (userCtx as { participantUUID?: string } | null)?.participantUUID ?? '';
      if (effectiveMeetingId && screenName) {
        try {
          const identifyRes = await fetch(`${config.api.baseUrl}/api/zoom/identify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              meetingId: effectiveMeetingId,
              screenName,
              participantUUID,
            }),
          });
          if (identifyRes.ok) {
            const identifyData = await identifyRes.json() as { data?: { token?: string } };
            const token = identifyData?.data?.token;
            if (token) {
              setInsightsToken(token);
              debugLog('[Zoom Identify] Token obtained for live insights');
            }
          } else {
            debugLog(`[Zoom Identify] No match (${identifyRes.status})`);
          }
        } catch (identifyErr: unknown) {
          debugLog(`[Zoom Identify] Failed: ${String((identifyErr as Error)?.message ?? identifyErr)}`);
        }
      }

      if (meetingCtx && userCtx) {
        try {
          await connectToBackend(meetingCtx, userCtx);
          await syncParticipantsToBackend();
        } catch {
          // ignore connection errors
        }
      }

      if (window.zoomSdk?.onParticipantChange) {
        try {
          await window.zoomSdk.onParticipantChange(async () => {
            await syncParticipantsToBackend();
          });
        } catch {
          // ignore
        }
      }

      initCompletedRef.current = true;
    } catch (err: unknown) {
      const message = String((err as Error)?.message ?? err);
      debugLog(`[Init] ERROR: ${message}`);
      setError(`Failed to initialize: ${message}`);
      setIsInitialized(true);
    } finally {
      initInProgressRef.current = false;
    }
  }, [connectToBackend, debugLog, fetchMeetingState, syncParticipantsToBackend]);

  const startDemoMode = useCallback(async () => {
    debugLog("[Demo] Starting demo mode...");

    const DEMO_MEETING_ID = "demo-meeting-12345";
    const DEMO_MEETING_UUID = "demo-meeting-uuid";

    const mockUserCtx = {
      participantUUID: "demo-host-uuid",
      role: "host",
      screenName: "Demo Host",
    };

    const mockMeetingCtx = {
      meetingUUID: DEMO_MEETING_UUID,
      meetingID: DEMO_MEETING_ID,
      meetingStartTime: new Date().toISOString(),
    };

    setUserContext(mockUserCtx);
    setMeetingContext(mockMeetingCtx);
    setMeetingId(DEMO_MEETING_ID);
    setMeetingUUID(DEMO_MEETING_UUID);
    setIsHost(true);

    const demoParticipants: Record<string, MeetingParticipant> = {
      "demo-host-uuid": {
        participantId: "demo-host-uuid",
        displayName: "Demo Host (You)",
        isHost: true,
        isCoHost: false,
        isBot: false,
        talk: { spokeMsTotal: 45000, spokeTurnsTotal: 3, lastTurnStartedAtMs: null },
        interrupt: { interruptCount: 0, lastInterruptAtMs: null, softMutedUntilMs: null },
      },
      p1: {
        participantId: "p1",
        displayName: "Alice Engineer",
        isHost: false,
        isCoHost: false,
        isBot: false,
        talk: { spokeMsTotal: 30000, spokeTurnsTotal: 2, lastTurnStartedAtMs: null },
        interrupt: { interruptCount: 1, lastInterruptAtMs: null, softMutedUntilMs: null },
      },
      p2: {
        participantId: "p2",
        displayName: "Bob Manager",
        isHost: false,
        isCoHost: false,
        isBot: false,
        talk: { spokeMsTotal: 15000, spokeTurnsTotal: 1, lastTurnStartedAtMs: null },
        interrupt: { interruptCount: 0, lastInterruptAtMs: null, softMutedUntilMs: null },
      },
    };

    setMeetingState({
      ...DEFAULT_MEETING_STATE,
      meetingId: DEMO_MEETING_ID,
      lifecycle: { status: "live", startedAtMs: Date.now() - 5 * 60 * 1000 },
      agenda: {
        items: [
          {
            agendaItemId: "t1",
            title: "Introductions",
            order: 0,
            plannedDurationSec: 60,
            status: "completed",
            startedAtMs: null,
            completedAtMs: null,
            timebox: { remainingSec: null, overtimeSec: null },
          },
          {
            agendaItemId: "t2",
            title: "Main discussion",
            order: 1,
            plannedDurationSec: 300,
            status: "active",
            startedAtMs: null,
            completedAtMs: null,
            timebox: { remainingSec: 120, overtimeSec: null },
          },
        ],
        activeAgendaItemId: "t2",
      },
      participants: demoParticipants,
      decisions: { items: [], consolidated: [] },
      actionsFeed: { items: [] },
      transcript: { currentSpeaker: { participantId: null, startedAtMs: null } },
      timestamp: Date.now(),
    });

    setIsInitialized(true);
    debugLog("[Demo] Demo mode initialized");
  }, [debugLog]);

  useEffect(() => {
    try {
      const logs = JSON.parse(localStorage.getItem("zoomAppLogs") || "[]");
      if (Array.isArray(logs)) {
        setDebugLogs(logs.slice(-config.ui.debugLogMaxEntries));
      }
    } catch {
      // ignore
    }
  }, []);

  const fetchBotStatus = useCallback(async () => {
    const uuid = meetingUUIDRef.current;
    if (!uuid) return;
    try {
      const raw = (await api.getBotStatus(uuid)) as { data?: { connected?: boolean; status?: string; message?: string }; connected?: boolean; status?: string; message?: string } | null;
      const data = raw && typeof raw === 'object' ? (raw.data ?? raw) : null;
      if (data && typeof data === 'object') {
        const connected = Boolean(data.connected);
        const statusStr = String(data.status ?? data.message ?? '');
        const meetingStateMsg =
          connected
            ? 'Bot connected, listening...'
            : statusStr
              ? `Bot: ${statusStr}`
              : 'Waiting for bot...';
        updateBotStatus({
          connected,
          meetingState: meetingStateMsg,
        });
      }
    } catch {
      // ignore - bot status is best-effort
    }
  }, [updateBotStatus]);

  useEffect(() => {
    if (!meetingId) return;

    fetchMeetingState(meetingId);

    if (meetingStatePollRef.current) clearInterval(meetingStatePollRef.current);
    meetingStatePollRef.current = setInterval(
      () => fetchMeetingState(meetingId),
      config.ui.agendaStatusPollInterval
    );

    if (backendSyncRef.current) clearInterval(backendSyncRef.current);
    // Sync participants immediately when meetingId is established (covers auto-start, late joiners)
    if (wsManagerRef.current?.isConnected()) syncParticipantsToBackend();
    backendSyncRef.current = setInterval(() => {
      if (wsManagerRef.current?.isConnected()) syncParticipantsToBackend();
    }, config.ui.backendSyncInterval);

    return () => {
      if (meetingStatePollRef.current) clearInterval(meetingStatePollRef.current);
      if (backendSyncRef.current) clearInterval(backendSyncRef.current);
    };
  }, [meetingId, fetchMeetingState, syncParticipantsToBackend]);

  useEffect(() => {
    if (!meetingUUID) return;
    fetchBotStatus();
    const botStatusInterval = setInterval(fetchBotStatus, config.ui.botStatusPollInterval);
    return () => clearInterval(botStatusInterval);
  }, [meetingUUID, fetchBotStatus]);

  useEffect(() => {
    return () => {
      if (meetingStatePollRef.current) clearInterval(meetingStatePollRef.current);
      if (backendSyncRef.current) clearInterval(backendSyncRef.current);
      if (wsManagerRef.current) wsManagerRef.current.disconnect();
    };
  }, []);

  const rollingTranscript = transcriptBuffer.slice(-ROLLING_TRANSCRIPT_DISPLAY_CHARS);

  const value = useMemo<MeetingStateContextValue>(
    () => ({
      meetingState,
      isInitialized,
      isHost,
      meetingId,
      meetingUUID,
      userContext,
      meetingContext,
      error,
      wsConnected,
      botStatus,
      rollingTranscript,
      debugLogs,
      initZoomApp,
      startDemoMode,
      startMeeting,
      advanceAgenda,
      skipAgenda,
      confirmDecision,
      getMeetingId,
      clearDebugLogs,
      screenshareOutputsScroll,
      insightsToken,
    }),
    [
      meetingState,
      isInitialized,
      isHost,
      meetingId,
      meetingUUID,
      userContext,
      meetingContext,
      error,
      wsConnected,
      botStatus,
      rollingTranscript,
      debugLogs,
      initZoomApp,
      startDemoMode,
      startMeeting,
      advanceAgenda,
      skipAgenda,
      confirmDecision,
      getMeetingId,
      clearDebugLogs,
      screenshareOutputsScroll,
      insightsToken,
    ]
  );

  return <MeetingStateContext.Provider value={value}>{children}</MeetingStateContext.Provider>;
}

export function useMeetingState(): MeetingStateContextValue {
  const context = useContext(MeetingStateContext);
  if (!context) throw new Error("useMeetingState must be used within a MeetingStateProvider");
  return context;
}

export default MeetingStateContext;
